import { DEG, type Mat3, type Vec3, clamp, enuToAltAz, mat3Apply, wrap180 } from '../lib/math';
import type { Source } from './skycam';

// Camera help: which of the bright stars and planets Ahillah predicts the camera actually sees. Plain sums with no page
// attached (tests/skymatch.test.ts).
//
// The phone says where it points, but its compass can be several degrees out, more near metal or indoors; its tilt is
// good to a degree or two. So the points of light in a frame are compared with the predictions allowing for one unknown
// turn about the vertical (the compass's error), with the camera's field of view known only roughly (a few widths are
// tried). A match has to be unlikely by chance before anything is ringed. Once one is found, the compass's error is
// known for a while (the lock), so even a single bright point where a planet should be can be trusted.

// ---------- The camera's lens ----------
/**
 * The frame against the screen: its size (px), its focal length (px, for the usual phone camera) and any quarter turn
 * that takes the frame's picture to the screen's (a frame still in the camera's own orientation while the phone is
 * held the other way: 1 counter-clockwise, −1 clockwise).
 */
export interface Lens {
  w: number;
  h: number;
  f: number;
  turn: 0 | 1 | -1;
}

/** The usual phone main camera's field across the frame's longer side (degrees): 26 mm equivalent gives 67°, 24 mm 72°,
 *  and steadying the video crops a little. Matching tries widths either side (SCALES), so it need not be exact (tune). */
export const FIELD = 66;
/** Focal lengths tried, as multiples of the usual: below 1 a wider field, above 1 a narrower one. */
export const SCALES = [0.86, 0.93, 1, 1.07, 1.15];

export const lensFor = (w: number, h: number, turn: Lens['turn'] = 0): Lens => ({ w, h, f: Math.max(w, h) / 2 / Math.tan((FIELD / 2) * DEG), turn });

/** The quarter turns to try: none when the frame and the screen are the same way round, else either way. */
export const turnsFor = (frameW: number, frameH: number, screenW: number, screenH: number): Lens['turn'][] =>
  frameW === frameH || screenW === screenH || frameW > frameH === screenW > screenH ? [0] : [1, -1];

/** A pixel of the frame as a direction in the camera's axes (the screen's right and up, and out of its face), for a
 *  focal length `s` times the lens's. */
export function pixelDir(l: Lens, x: number, y: number, s = 1): Vec3 {
  let X = (x - l.w / 2) / (l.f * s), Y = -(y - l.h / 2) / (l.f * s);
  if (l.turn === 1) [X, Y] = [-Y, X];
  else if (l.turn === -1) [X, Y] = [Y, -X];
  const n = Math.hypot(X, Y, 1);
  return [X / n, Y / n, -1 / n];
}

/** Where a direction in the camera's axes falls in the frame (px), or null if it is behind the camera. */
export function framePoint(l: Lens, c: Vec3, s = 1): { x: number; y: number } | null {
  if (c[2] > -1e-6) return null;
  let X = c[0] / -c[2], Y = c[1] / -c[2];
  if (l.turn === 1) [X, Y] = [Y, -X];
  else if (l.turn === -1) [X, Y] = [-Y, X];
  return { x: l.w / 2 + X * l.f * s, y: l.h / 2 - Y * l.f * s };
}

/** The sky the frame covers, in square degrees (roughly: its width times its height in angle). */
export function frameArea(l: Lens, s = 1): number {
  return ((2 * Math.atan(l.w / 2 / (l.f * s))) / DEG) * ((2 * Math.atan(l.h / 2 / (l.f * s))) / DEG);
}

/** ENU into the camera's axes, for a camera→ENU matrix (its transpose). */
export const toCamera = (m: Mat3, v: Vec3): Vec3 => [
  m[0] * v[0] + m[3] * v[1] + m[6] * v[2],
  m[1] * v[0] + m[4] * v[1] + m[7] * v[2],
  m[2] * v[0] + m[5] * v[1] + m[8] * v[2],
];
const forwardOf = (m: Mat3): Vec3 => [-m[2], -m[5], -m[8]];
const angle = (a: Vec3, b: Vec3) => Math.acos(clamp(a[0] * b[0] + a[1] * b[1] + a[2] * b[2], -1, 1)) / DEG;

// ---------- Matching ----------
/** Something Ahillah expects the camera may see: its key ('star:12', 'body:Jupiter'), its direction as it appears (ENU,
 *  refraction included) and how bright it looks from here (magnitude, dimmed by the air at its height). */
export interface Prediction {
  key: string;
  enu: Vec3;
  mag: number;
}

export const MATCH = {
  /** A point of light within this many degrees of where a prediction falls is it, at the middle of the frame (tune)... */
  tol: 0.9,
  /** ...and this much more for each degree out from the middle, as the field's width is known only roughly (tune). */
  tolGrow: 0.025,
  /** Once lined up, the field's width and the compass's error are known: the reach is this, and grows this much (tune). */
  lockTol: 0.6,
  lockGrow: 0.015,
  /** The compass may be this far out (degrees)... */
  wide: 25,
  /** ...and the phone's sense of its tilt this far, more while it moves (degrees, tune). */
  tilt: 3,
  /** Once lined up, the compass's error and the tilt's move no more than these between frames (degrees). */
  lockWin: 2,
  lockTilt: 1.5,
  /** Fitting the pairs found: the field's width may move this share either way, the frame turn this many degrees about
   *  the middle (the phone's roll a little out) and shift this far (degrees). */
  fitScale: 0.05,
  fitRoll: 5,
  fitShift: 3,
  /**
   * A solution must turn up by chance less often than this to count, with no lock. Over 4,500 made-up frames of 2 to 17
   * random points of light, 0.07% were wrongly sure at this level; of 109 views of the real sky over Wembley with three
   * or more bright stars caught, the compass up to 20° out and the tilt and roll up to 2° and 3°, 105 were sure, and none
   * paired a star with another's light (tests/skymatch.test.ts checks both, more briefly).
   */
  chance: 0.0003,
  /**
   * With a lock, where a single point can count. In the same frames about 1% of random points were still wrongly sure
   * at this level, so fewer than three found at once are ringed only once found in two looks running (JUDGE.confirm,
   * JUDGE.atOnce): a plane's light or a speck of noise does not stay on a star's place.
   */
  chanceLocked: 0.005,
  /** Only the brightest predictions and points of light are compared. */
  preds: 14,
  sources: 20,
};

/**
 * A way the frame may lie on the sky: a quarter turn, a field width (focal length multiple), the compass's error
 * (degrees, added to every prediction's compass direction) and the tilt's (degrees, added to every prediction's height).
 */
export interface Fit {
  turn: Lens['turn'];
  scale: number;
  delta: number;
  tilt: number;
}
export interface Match {
  key: string;
  /** Which point of light (an index into the sources given), and how far apart they are (degrees). */
  src: number;
  sep: number;
}
export interface Solution extends Fit {
  matches: Match[];
  /** How many solutions as good would turn up by chance (chanceOf). */
  chance: number;
}

function binom(n: number, k: number): number {
  let r = 1;
  for (let i = 0; i < k; i++) r = (r * (n - i)) / (i + 1);
  return r;
}

/**
 * How many alignments as good as `k` matches would turn up by chance, were the `D` points of light nothing to do with
 * the `P` predictions and scattered at random over a frame of `area` square degrees. The first match may fall anywhere in
 * the box searched (`win` degrees of sky either way across, `high` either way up and down), which fixes the compass's
 * and the tilt's errors; each further one must then land within `tol` of its own prediction. Summed over the ways of
 * choosing which predictions and points match, and over the `tries` (field widths, frame turns) searched as if each
 * were a fresh chance. Rough, and on the cautious side.
 */
export function chanceOf(k: number, P: number, D: number, area: number, win: number, high: number, tol: number, tries: number): number {
  if (k <= 0) return 1;
  if (k > Math.min(P, D)) return 0;
  const first = Math.min(1, (2 * win * 2 * Math.max(high, tol)) / area);
  const next = Math.min(1, (Math.PI * tol * tol) / area);
  let ways = binom(P, k);
  for (let i = 0; i < k; i++) ways *= D - i;
  return tries * ways * first * next ** (k - 1);
}

interface At {
  enu: Vec3;
  alt: number;
  az: number;
}

/** The camera's own axes in ENU: to its right, up the frame, and where it looks. */
interface Axes {
  right: Vec3;
  up: Vec3;
  fwd: Vec3;
}
const axesOf = (m: Mat3): Axes => ({ right: [m[0], m[3], m[6]], up: [m[1], m[4], m[7]], fwd: [-m[2], -m[5], -m[8]] });
const dotV = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/** A direction turned about the vertical so its compass direction grows by `deg` degrees. */
function turnAround(v: Vec3, deg: number): Vec3 {
  const c = Math.cos(deg * DEG), s = Math.sin(deg * DEG);
  return [v[0] * c + v[1] * s, v[1] * c - v[0] * s, v[2]];
}
/** A direction turned `deg` degrees about the camera's right-hand axis, so up the frame (Rodrigues' formula). */
function turnUp(v: Vec3, deg: number, k: Vec3): Vec3 {
  const c = Math.cos(deg * DEG), s = Math.sin(deg * DEG), kv = dotV(k, v) * (1 - c);
  return [
    v[0] * c + (k[1] * v[2] - k[2] * v[1]) * s + k[0] * kv,
    v[1] * c + (k[2] * v[0] - k[0] * v[2]) * s + k[1] * kv,
    v[2] * c + (k[0] * v[1] - k[1] * v[0]) * s + k[2] * kv,
  ];
}
/**
 * A prediction where the phone's errors put it: the compass's (`delta`, a turn about the vertical) and then the tilt's
 * (`tilt`, a turn about the camera's own right-hand axis, so up or down the frame, as a phone's sense of its pitch is
 * out). Near the horizon the tilt is a change of height; high up, where the frame reaches over the zenith, it is not.
 */
const moved = (enu: Vec3, delta: number, tilt: number, ax: Axes): Vec3 => turnUp(turnAround(enu, delta), tilt, ax.right);

/** How far up the frame a direction is, as an angle in the camera's up-and-forward plane (degrees). */
const upAngle = (v: Vec3, ax: Axes) => Math.atan2(dotV(v, ax.up), dotV(v, ax.fwd)) / DEG;
/** The compass direction of a direction (degrees). */
const azOf = (v: Vec3) => Math.atan2(v[0], v[1]) / DEG;

/** The compass and tilt errors that put prediction `e` exactly on point of light `d` (up and down the frame and across
 *  it), found by turns in turn, twice over. */
function errorsFor(e: Vec3, d: Vec3, ax: Axes): { delta: number; tilt: number } {
  let delta = wrap180(azOf(d) - azOf(e)), tilt = 0;
  for (let k = 0; k < 2; k++) {
    tilt = upAngle(d, ax) - upAngle(turnAround(e, delta), ax);
    delta = wrap180(azOf(turnUp(d, -tilt, ax.right)) - azOf(e));
  }
  return { delta, tilt };
}

/** The predictions matched one to one with the points of light, nearest first, for compass and tilt errors. */
function assign(preds: (At & { tol: number })[], dets: (At | null)[], delta: number, tilt: number, ax: Axes): { matches: Match[]; resid: number } {
  const pairs: { j: number; i: number; sep: number }[] = [];
  for (let j = 0; j < preds.length; j++) {
    const p = preds[j];
    const e = moved(p.enu, delta, tilt, ax);
    const alt = Math.asin(clamp(e[2], -1, 1)) / DEG;
    for (let i = 0; i < dets.length; i++) {
      const d = dets[i];
      if (!d || Math.abs(d.alt - alt) > p.tol) continue;
      const sep = angle(e, d.enu);
      if (sep <= p.tol) pairs.push({ j, i, sep });
    }
  }
  pairs.sort((a, b) => a.sep - b.sep);
  const usedJ = new Set<number>(), usedI = new Set<number>();
  const matches: Match[] = [];
  let resid = 0;
  for (const q of pairs) {
    if (usedJ.has(q.j) || usedI.has(q.i)) continue;
    usedJ.add(q.j);
    usedI.add(q.i);
    matches.push({ key: String(q.j), src: q.i, sep: q.sep });
    resid += q.sep / preds[q.j].tol;
  }
  return { matches, resid };
}

/**
 * The best way to line up the points of light in a frame (`sources`, brightest first) with the predictions (brightest
 * first), for the phone held as `camToEnu` says: each quarter turn in `turns` and field width in `scales` is tried, and
 * every compass error within `MATCH.wide` degrees and tilt error within `MATCH.tilt` that would put a prediction on a
 * point of light. Most matches wins; then the smaller errors, then the closer fit. With a `lock` (a fit found before),
 * only that turn and width, and errors within `MATCH.lockWin` and `MATCH.lockTilt` of its own. Only predictions that some
 * error searched would put in the frame take part. The best is then fitted exactly (refine), and its chance of turning
 * up at random weighed on the pairs found before that fit which it keeps. Null with nothing to compare.
 */
export function matchSky(
  predictions: Prediction[],
  sources: Source[],
  lens: Lens,
  camToEnu: Mat3,
  o: { turns?: Lens['turn'][]; scales?: number[]; lock?: Fit | null } = {},
): Solution | null {
  const lock = o.lock ?? null;
  const turns = lock ? [lock.turn] : o.turns ?? [lens.turn];
  const scales = lock ? [lock.scale] : o.scales ?? SCALES;
  const win = lock ? MATCH.lockWin : MATCH.wide, high = lock ? MATCH.lockTilt : MATCH.tilt;
  const centre = lock ? lock.delta : 0, level = lock ? lock.tilt : 0;
  // In the frame for some error searched, some way round, at the widest field tried.
  const widest = Math.min(...scales);
  const ax = axesOf(camToEnu);
  const couldShow = (p: Prediction) => {
    return [-1, 0, 1].some((a) => [-1, 0, 1].some((b) => {
      const c = toCamera(camToEnu, moved(p.enu, centre + a * win, level + b * high, ax));
      return turns.some((turn) => {
        const q = framePoint({ ...lens, turn }, c, widest);
        return !!q && q.x >= 0 && q.y >= 0 && q.x <= lens.w && q.y <= lens.h;
      });
    }));
  };
  const P = predictions.filter(couldShow).slice(0, MATCH.preds), S = sources.slice(0, MATCH.sources);
  if (!P.length || !S.length) return null;
  const fwd = forwardOf(camToEnu);
  const [tol0, grow] = lock ? [MATCH.lockTol, MATCH.lockGrow] : [MATCH.tol, MATCH.tolGrow];
  const preds = P.map((p) => ({ enu: p.enu, ...enuToAltAz(p.enu), tol: tol0 + grow * angle(p.enu, fwd) }));
  let best: (Fit & { matches: Match[]; score: number; D: number }) | null = null;
  for (const turn of turns) {
    const l = { ...lens, turn };
    for (const scale of scales) {
      // The points of light as directions on the sky; those below the horizon (roofs, street lights) cannot match.
      const dets = S.map((s): At | null => {
        const enu = mat3Apply(camToEnu, ...pixelDir(l, s.x, s.y, scale));
        const at = enuToAltAz(enu);
        return at.alt > -1 ? { enu, ...at } : null;
      });
      const D = dets.filter(Boolean).length;
      // Each pair of compass and tilt errors that puts a prediction on a point of light (to a fifth of a degree).
      const tried = new Set<string>();
      for (const p of preds) {
        for (const d of dets) {
          // Heights first, cheaply: a tilt error moves things at most its own size (and the frame's turn a little).
          if (!d || Math.abs(d.alt - p.alt) > Math.abs(level) + high + 2) continue;
          const { delta, tilt } = errorsFor(p.enu, d.enu, ax);
          const off = wrap180(delta - centre), up = tilt - level;
          if (Math.abs(up) > high || Math.abs(off) > win) continue;
          const key = `${Math.round(delta * 5)},${Math.round(tilt * 5)}`;
          if (tried.has(key)) continue;
          tried.add(key);
          const { matches, resid } = assign(preds, dets, delta, tilt, ax);
          const k = matches.length;
          if (!k) continue;
          const score = k - 0.25 * (resid / k) - Math.abs(off) / (4 * win) - Math.abs(up) / (4 * high);
          if (!best || score > best.score) best = { turn, scale, delta, tilt, matches, score, D };
        }
      }
    }
  }
  if (!best) return null;
  const l = { ...lens, turn: best.turn };
  const fit = refine(preds, S, l, camToEnu, best);
  // The evidence: the pairs the search found that the exact fit keeps. Pairs the fit adds are ringed, but are not
  // counted, as the fit was made to agree with them.
  const before = new Set(best.matches.map((m) => `${m.key}:${m.src}`));
  const kept = fit.pairs.filter((m) => before.has(`${m.j}:${m.i}`));
  const k = kept.length;
  // The search box in degrees of sky: an error in compass direction moves things less, higher up.
  const sky = Math.max(Math.cos(enuToAltAz(fwd).alt * DEG), 0.17);
  const tol = k ? kept.reduce((s, m) => s + preds[m.j].tol, 0) / k : MATCH.tol;
  // Chance is weighed among the predictions and points of light at least as bright as the faintest matched (both lists
  // run brightest first): bright stars matching the brightest points is far less likely by chance than faint ones
  // matching any of many. Points below the horizon never count.
  const lastJ = Math.max(-1, ...kept.map((m) => m.j)), lastI = Math.max(-1, ...kept.map((m) => m.i));
  const below = S.map((s) => mat3Apply(camToEnu, ...pixelDir(l, s.x, s.y, best!.scale))[2] <= Math.sin(-1 * DEG));
  const dEff = below.slice(0, lastI + 1).filter((b) => !b).length;
  const chance = chanceOf(k, lastJ + 1, dEff, frameArea(l, best.scale), win * sky, high, tol, turns.length * scales.length);
  return {
    turn: best.turn, scale: fit.scale, delta: best.delta, tilt: best.tilt, chance,
    matches: fit.pairs.map((m) => ({ key: P[m.j].key, src: m.i, sep: m.sep })),
  };
}

/**
 * The pairs found, made exact. In the camera's own plane, with the compass's and the tilt's errors already allowed for,
 * what is left is fitted to them: a little more or less field width (MATCH.fitScale), a small turn about the middle (the
 * phone's roll a little out, MATCH.fitRoll) and a small shift (MATCH.fitShift). Then everything is matched again, as
 * tightly as the pairs agree (three times their spread, but no tighter than half a degree, or 0.8° with only two to
 * judge by, nor looser than the search at the middle), keeping only pairs whose star the fit puts inside the frame.
 * Without the turn, a roll would be taken up as a change of width, and a star outside the frame could be paired with
 * another's light inside it. Twice over.
 */
function refine(preds: (At & { tol: number })[], S: Source[], l: Lens, camToEnu: Mat3, best: Fit & { matches: Match[] }): { pairs: { j: number; i: number; sep: number }[]; scale: number } {
  const plane = (c: Vec3): [number, number] | null => (c[2] < -1e-6 ? [c[0] / -c[2], c[1] / -c[2]] : null);
  const ax = axesOf(camToEnu);
  const P = preds.map((p) => plane(toCamera(camToEnu, moved(p.enu, best.delta, best.tilt, ax))));
  const D = S.map((s) => {
    const c = pixelDir(l, s.x, s.y, best.scale);
    return mat3Apply(camToEnu, ...c)[2] > Math.sin(-1 * DEG) ? plane(c) : null;
  });
  const fwd = forwardOf(camToEnu);
  const off = preds.map((p) => angle(p.enu, fwd));
  let pairs = best.matches.map((m) => ({ j: +m.key, i: m.src, sep: m.sep })).filter((q) => P[q.j] && D[q.i]);
  let s = 1;
  for (let round = 0; round < 2 && pairs.length >= 2; round++) {
    // Least squares for P ≈ s·R·D + t over the pairs (R a turn by r): the means first, then the rest about them.
    let dx = 0, dy = 0, px = 0, py = 0;
    for (const q of pairs) {
      dx += D[q.i]![0];
      dy += D[q.i]![1];
      px += P[q.j]![0];
      py += P[q.j]![1];
    }
    const n = pairs.length;
    [dx, dy, px, py] = [dx / n, dy / n, px / n, py / n];
    let sa = 0, sb = 0, sd = 0;
    for (const q of pairs) {
      const ax = D[q.i]![0] - dx, ay = D[q.i]![1] - dy, bx = P[q.j]![0] - px, by = P[q.j]![1] - py;
      sa += ax * bx + ay * by;
      sb += ax * by - ay * bx;
      sd += ax * ax + ay * ay;
    }
    s = sd > 1e-12 ? clamp(Math.hypot(sa, sb) / sd, 1 - MATCH.fitScale, 1 + MATCH.fitScale) : 1;
    const r = sd > 1e-12 ? clamp(Math.atan2(sb, sa), -MATCH.fitRoll * DEG, MATCH.fitRoll * DEG) : 0;
    const a = s * Math.cos(r), b = s * Math.sin(r);
    const lim = MATCH.fitShift * DEG;
    const tx = clamp(px - (a * dx - b * dy), -lim, lim), ty = clamp(py - (b * dx + a * dy), -lim, lim);
    const miss = (j: number, i: number) => Math.hypot(P[j]![0] - (a * D[i]![0] - b * D[i]![1] + tx), P[j]![1] - (b * D[i]![0] + a * D[i]![1] + ty)) / DEG;
    // Where the fit puts a star in the frame: inside it (or within a few pixels of its edge), or it cannot be there.
    const inFrame = (j: number) => {
      const x = P[j]![0] - tx, y = P[j]![1] - ty, d = s * s;
      const q = framePoint(l, [(a * x + b * y) / d, (a * y - b * x) / d, -1], best.scale);
      return !!q && q.x >= -3 && q.y >= -3 && q.x <= l.w + 3 && q.y <= l.h + 3;
    };
    const spread = Math.sqrt(pairs.reduce((acc, q) => acc + miss(q.j, q.i) ** 2, 0) / n);
    const tight = clamp(3 * spread, n >= 3 ? 0.5 : 0.8, MATCH.tol);
    const all: { j: number; i: number; sep: number }[] = [];
    for (let j = 0; j < P.length; j++) {
      if (!P[j] || !inFrame(j)) continue;
      for (let i = 0; i < D.length; i++) {
        if (!D[i]) continue;
        const sep = miss(j, i);
        if (sep <= tight + 0.01 * off[j]) all.push({ j, i, sep });
      }
    }
    all.sort((x, y) => x.sep - y.sep);
    const usedJ = new Set<number>(), usedI = new Set<number>();
    pairs = all.filter((q) => !usedJ.has(q.j) && !usedI.has(q.i) && (usedJ.add(q.j), usedI.add(q.i), true));
  }
  return { pairs, scale: best.scale / s };
}

/**
 * The bright predictions the camera should surely catch in this frame if the sky were clear: no fainter than `limit`,
 * at least 15° up, and inside the frame by `margin` degrees (for every way round it may be), the frame placed by `fit`
 * where one is known (its compass and tilt errors, width and way round). The margin leaves room for how far the
 * compass may be out, or have drifted since the fit.
 */
export function surePredictions(predictions: Prediction[], lens: Lens, camToEnu: Mat3, limit: number, fit: Fit | null, margin: number, turns: Lens['turn'][] = [lens.turn]): string[] {
  const s = fit ? fit.scale : 1;
  const m = margin * DEG * lens.f * s;
  const ax = axesOf(camToEnu);
  const out: string[] = [];
  for (const p of predictions) {
    if (p.mag > limit || p.enu[2] < Math.sin(15 * DEG)) continue;
    const c = toCamera(camToEnu, fit ? moved(p.enu, fit.delta, fit.tilt, ax) : p.enu);
    const inside = (fit ? [fit.turn] : turns).every((turn) => {
      const q = framePoint({ ...lens, turn }, c, s);
      return !!q && q.x >= m && q.x <= lens.w - m && q.y >= m && q.y <= lens.h - m;
    });
    if (inside) out.push(p.key);
  }
  return out;
}

// ---------- Judging, over a few seconds ----------
/** What one steady frame showed. */
export interface Look {
  /** The best fit from a full search, and the best near the lock (if there is one). */
  free: Solution | null;
  locked: Solution | null;
  /** Bright predictions the camera should surely catch here (surePredictions), by key. */
  sure: string[];
  /** Points of light above the horizon. */
  sources: number;
  /** How patchy the sky is (skycam.ts, texture). */
  texture: { rel: number; abs: number };
  /** The sky's brightness from the camera's matched stars, and the model's for a clear sky there (mag/arcsec²; NaN
   *  where unknown). */
  sky: number;
  model: number;
  /** Each prediction's magnitude, by key. */
  mags: Map<string, number>;
  /** This frame may not speak about cloud: the camera has only just opened (its brightness still settling), a lamp's
   *  glare is in it, or it is all but black (a finger over the lens, say). */
  quiet?: boolean;
}

/** What the camera says now: the predictions it has found (keys), and a note when cloud or haze seems to be in the way. */
export interface Verdict {
  found: string[];
  note: 'cloud' | 'haze' | null;
}

export const JUDGE = {
  /** A ring stays this long after the camera last found its object (ms). */
  hold: 4000,
  /** Under a lock, fewer than this many found at once are ringed only once each is found again within `confirm`. */
  atOnce: 3,
  /** A point found under a lock is ringed once found again within this long (ms). */
  confirm: 1500,
  /** A lock lasts this long after the camera last lined up with the sky (ms). */
  lockFor: 60000,
  /** The camera is sure of objects this bright to start with (magnitude, tune)... */
  sureFrom: 1,
  /** ...and of fainter ones, down to this, once it has shown it can catch them. */
  sureTo: 2,
  /** Frames, and how long they must span (ms), before cloud is said to hide the sky; only frames this recent count. */
  hiddenFrames: 5,
  hiddenSpan: 2000,
  recent: 3000,
  /** Lit cloud is patchy: the sky's strays from a smooth surface (skycam.ts, texture), as a share of its level and in
   *  video values. Made-up frames give 0.11 to 0.15 for patchy cloud and under 0.02 for a clear or hazy sky (tune). */
  patchyRel: 0.05,
  patchyAbs: 1.5,
  /** Haze: the camera's sky this much brighter than the model's for a clear sky (mag/arcsec², tune). */
  hazeMag: 1.5,
  /** Once the lock has lapsed, the last fit still places the frame for the stars it should surely catch, give or take
   *  this many degrees (the compass drifts slowly). */
  drift: 6,
};

export class CameraJudge {
  /** The compass's and tilt's errors once lined up with the sky, and when it last was. */
  lock: (Fit & { at: number }) | null = null;
  /** The last fit found, kept after the lock lapses (switched off, it is forgotten). */
  last: Fit | null = null;
  /** The faintest the camera should surely catch (magnitude): JUDGE.sureFrom, raised once it catches fainter. */
  sureMag = JUDGE.sureFrom;
  /**
   * The camera has found a star or planet since camera help was switched on. Until it has, finding nothing says nothing
   * about cloud: some phones' video cannot catch stars at all.
   */
  proven = false;
  private seen = new Map<string, number>();
  /** Single points found under the lock, waiting to be found again (key, when). */
  private single = new Map<string, number>();
  private history: { at: number; matched: number; sure: number; sources: number; patchy: boolean; dim: boolean; quiet: boolean }[] = [];

  /** Start again: nothing found, no lock, nothing remembered (camera help off, or another place). */
  reset() {
    this.lock = null;
    this.last = null;
    this.sureMag = JUDGE.sureFrom;
    this.proven = false;
    this.seen.clear();
    this.single.clear();
    this.history = [];
  }

  /**
   * What the recent frames saw no longer describes the sky in view (another time shown, pointing paused): their rings
   * and their say about cloud go. The compass's error and what the camera has shown it catches are kept.
   */
  forgetFrames() {
    this.history = [];
    this.seen.clear();
    this.single.clear();
  }

  /** Where the frame lies for the stars it should surely catch: the lock while it lasts, else the last fit, with room
   *  for drift; with neither, room for the compass to be out by a third of the most the search allows. */
  get placing(): { fit: Fit | null; margin: number } {
    if (this.lock) return { fit: this.lock, margin: MATCH.lockWin + 1 };
    if (this.last) return { fit: this.last, margin: JUDGE.drift };
    return { fit: null, margin: MATCH.wide / 3 };
  }

  /** The rings past their hold go even while no frame is judged (the phone turning, north being found): what is left. */
  tidy(now: number): string[] {
    for (const [k, until] of this.seen) if (until <= now) this.seen.delete(k);
    return [...this.seen.keys()];
  }

  /** A steady frame's look at the sky, at `now` (ms). */
  update(look: Look, now: number): Verdict {
    if (this.lock && now - this.lock.at > JUDGE.lockFor) this.lock = null;
    const f = look.free, l = look.locked;
    const freeOk = !!f && f.matches.length >= 2 && f.chance < MATCH.chance;
    const lockOk = !!this.lock && !!l && l.matches.length >= 1 && l.chance < MATCH.chanceLocked;
    // A full search that finds more than the lock does wins (the compass has been corrected, say); else the lock's.
    let use = freeOk && (!lockOk || f!.matches.length > l!.matches.length) ? f : lockOk ? l : null;
    for (const [k, at] of this.single) if (now - at > JUDGE.confirm) this.single.delete(k);
    // From the lock's own search: its errors are eased towards, not jumped to (a few pairs, roll and all, move them).
    const fromLock = !!use && use === l;
    // Found under the lock alone, fewer than three at once: each is ringed only once found in two looks running.
    if (use && fromLock && use.matches.length < JUDGE.atOnce) {
      const confirmed = use.matches.filter((m) => this.single.has(m.key));
      for (const m of use.matches) this.single.set(m.key, now);
      use = confirmed.length ? { ...use, matches: confirmed } : null;
    }
    if (use) {
      const was = this.lock && fromLock ? this.lock : null;
      const delta = was ? was.delta + 0.3 * wrap180(use.delta - was.delta) : use.delta;
      const tilt = was ? was.tilt + 0.3 * (use.tilt - was.tilt) : use.tilt;
      this.lock = { turn: use.turn, scale: use.scale, delta, tilt, at: now };
      this.last = { turn: use.turn, scale: use.scale, delta, tilt };
      this.proven = true;
      for (const m of use.matches) {
        this.seen.set(m.key, now + JUDGE.hold);
        // Caught something fainter than it was sure of: it should catch a little less faint ones from now on.
        const mag = look.mags.get(m.key);
        if (mag !== undefined) this.sureMag = clamp(Math.max(this.sureMag, mag - 0.7), JUDGE.sureFrom, JUDGE.sureTo);
      }
    }
    const matched = new Set(use?.matches.map((m) => m.key));
    const patchy = look.texture.rel >= JUDGE.patchyRel && look.texture.abs >= JUDGE.patchyAbs;
    // Stars found, yet the sky far brighter than a clear one here would be and a sure one missing: dimmed by haze. Not
    // from a frame that may not speak about cloud (a lamp's glare brightens its sky too).
    const dim = !look.quiet && matched.size > 0 && look.sky < look.model - JUDGE.hazeMag && look.sure.some((k) => !matched.has(k));
    this.history.push({ at: now, matched: matched.size, sure: look.sure.length, sources: look.sources, patchy, dim, quiet: !!look.quiet });
    while (this.history.length && now - this.history[0].at > JUDGE.recent) this.history.shift();
    for (const [k, until] of this.seen) if (until <= now) this.seen.delete(k);
    const { note, hidden } = this.note();
    // Hidden: the rings still held from a moment ago no longer stand for anything the camera sees.
    if (hidden) this.seen.clear();
    return { found: [...this.seen.keys()], note };
  }

  /**
   * What is said about the sky from the last few frames, once the camera has shown it can catch stars: several seconds
   * of bright stars expected and no point of light at all in most frames is hidden (by patchy cloud, or by haze or an
   * even layer of cloud); stars found in a sky far brighter than a clear one, with a sure one missing, is haze.
   */
  private note(): { note: Verdict['note']; hidden: boolean } {
    const h = this.history;
    // Nothing either while the newest frame may not speak about cloud: frames before it keep no note on screen.
    if (!this.proven || h.length < JUDGE.hiddenFrames || h[h.length - 1].quiet || h[h.length - 1].at - h[0].at < JUDGE.hiddenSpan) return { note: null, hidden: false };
    const counts = h.map((x) => x.sources).sort((a, b) => a - b);
    if (h.every((x) => x.sure > 0 && x.matched === 0 && !x.quiet) && counts[counts.length >> 1] === 0) return { note: h[h.length - 1].patchy ? 'cloud' : 'haze', hidden: true };
    return { note: h.filter((x) => x.dim).length > h.length / 2 ? 'haze' : null, hidden: false };
  }
}
